import { SafetyCertificateOutlined } from '@ant-design/icons';
import { nameNormalize } from '@mahawi/eshop-common/src/name-normalize';
import { type IAddress } from '@mahawi/eshop-common/src/types';
import { Button, Space, Tag, Typography } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { orderVerifyVatId } from '../../reducers/order/reducer';
import { type IVatIdVerificationResult } from '../../reducers/order/types';
import { SPACING } from '../../theme/tokens';
import AddressDisplay from '../address-display';
import CopyToClipboard from '../copy-to-clipboard';

const { Text } = Typography;

function VatIdVerificationResult({
  result,
}: {
  result: IVatIdVerificationResult;
}): React.ReactElement {
  return (
    <Space orientation="vertical" size={SPACING.none}>
      <Space size={SPACING.xs} align="baseline">
        {result.isValid === true && <Tag color="green">VIES valid</Tag>}
        {result.isValid === false && <Tag color="red">VIES invalid</Tag>}
        {result.isValid === null && <Tag color="orange">VIES unavailable</Tag>}
        <Text type="secondary">{result.vatId}</Text>
        {result.requestDate && (
          <Text type="secondary">{result.requestDate}</Text>
        )}
      </Space>
      {result.name && <Text>{result.name}</Text>}
      {result.address && <Text type="secondary">{result.address}</Text>}
      {result.errorMessage && <Text type="danger">{result.errorMessage}</Text>}
    </Space>
  );
}

interface IBillingAddressSectionProps {
  order: {
    uuid: string;
    isForCompany: boolean;
    deliveryAddressIsDifferent: boolean;
    billingAddress: IAddress;
  };
}

export const BillingAddressSection = React.memo(function BillingAddressSection({
  order,
}: IBillingAddressSectionProps): React.ReactElement {
  const { billingAddress } = order;
  const dispatch = useAppDispatch();
  const vatIdVerificationInProcess: boolean = useSelector(
    (s: RootState) => s.Order.vatIdVerificationInProcess,
  );
  const vatIdVerification: IVatIdVerificationResult | null = useSelector(
    (s: RootState) => s.Order.vatIdVerification,
  );
  const companyNameNormalized: string = nameNormalize(
    billingAddress.companyName || '',
  );

  return (
    <Space orientation="vertical">
      {order.isForCompany && (
        <Tag key="isForCompany" color="green" security="true">
          Is for company
        </Tag>
      )}

      {!order.isForCompany && (
        <Tag key="isForPerson" color="blue" security="true">
          Is for person
        </Tag>
      )}

      {order.deliveryAddressIsDifferent && (
        <Tag key="deliveryAddressIsDifferent" color="red">
          Delivery address is different
        </Tag>
      )}

      {order.isForCompany && (
        <>
          <Space size={SPACING.xs} align="baseline">
            <Text>company name:</Text>
            <Text strong>{companyNameNormalized || EMPTY_VALUE}</Text>
            {billingAddress.companyName && (
              <CopyToClipboard text={companyNameNormalized} type="link" />
            )}
          </Space>

          <Space size={SPACING.xs} align="baseline">
            <Text>company ID:</Text>
            <Text strong>{billingAddress.companyId || EMPTY_VALUE}</Text>
            {billingAddress.companyId && (
              <CopyToClipboard text={billingAddress.companyId} type="link" />
            )}
          </Space>

          <Space size={SPACING.xs} align="baseline">
            <Text>company VAT ID:</Text>
            <Text strong>{billingAddress.companyTaxId || EMPTY_VALUE}</Text>
            {billingAddress.companyTaxId && (
              <CopyToClipboard text={billingAddress.companyTaxId} type="link" />
            )}
            {billingAddress.companyTaxId && (
              <Button
                size="small"
                icon={<SafetyCertificateOutlined />}
                loading={vatIdVerificationInProcess}
                disabled={vatIdVerificationInProcess}
                onClick={(): void => {
                  dispatch(orderVerifyVatId(order.uuid));
                }}
              >
                Verify in VIES
              </Button>
            )}
          </Space>

          {vatIdVerification && (
            <VatIdVerificationResult result={vatIdVerification} />
          )}
        </>
      )}

      <AddressDisplay address={billingAddress} />
    </Space>
  );
});
